import React, { useCallback, useState } from 'react';

import { Alert, Chip, IconButton } from '@mui/material';
import type { GridColDef, GridToolbarProps } from '@mui/x-data-grid-premium';
import {
  DataGridPremium,
  gridRowSelectionIdsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { Helmet } from 'react-helmet';
import { AlineInput } from 'src/AlineInput';
import {
  AlineButton,
  AlineCheckbox,
  AlineTooltip,
  ConfirmationDialog,
  ListEmpty,
  SearchInput,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { WhitePlusIcon } from 'src/components/icons/WhitePlusIcon';
import { useDeleteApiKeys, useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDispatchNotification } from 'src/helpers';
import { createDataGridPremiumFooterlessPaperStyles, useDataGridSearch } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { CreateApiKeyModal } from './CreateApiKeyModal';

const useStyles = makeStyles()(theme => ({
  deleteWrapper: {
    padding: theme.spacing(2, 0),
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  permissionChips: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    minHeight: '100%',
    padding: theme.spacing(0.5, 0),
  },
  xGrid: {
    flexGrow: 1,
    minHeight: 0,
  },
}));

interface ApiKey {
  __typename: 'ApiKey';
  createdAt: string;
  id: string;
  lastUsedAt: string | null;
  name: string;
  obfuscatedValue: string;
  permissions: string[];
}

const columns: GridColDef<ApiKey>[] = [
  {
    field: 'name',
    flex: 1,
    headerName: 'Name',
  },
  {
    field: 'obfuscatedValue',
    flex: 1,
    headerName: 'Key',
    renderCell: params => <code>{params.value}</code>,
  },
  {
    field: 'permissions',
    flex: 2,
    headerName: 'Permissions',
    renderCell: params => <PermissionChips permissions={params.row.permissions} />,
  },
  {
    field: 'createdAt',
    flex: 1,
    headerName: 'Created',
    valueFormatter: value => new Date(value).toLocaleDateString(),
  },
];

const confirmDeleteString = 'DELETE';

function PermissionChips({ permissions }: { permissions: string[] }): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.permissionChips}>
      {permissions.map(permission => (
        <Chip key={permission} label={permission} size="small" variant="outlined" />
      ))}
    </div>
  );
}

export function ApiKeysPage(): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const isAdmin = currentUser?.role === Apollo.OrgRole.Admin;
  const { deleteApiKeys } = useDeleteApiKeys();
  const { data, error: queryError, loading, refetch } = Apollo.useApiKeysQuery();

  const apiKeys = data?.apiKeys ?? [];
  const error = queryError?.message ?? null;

  const [deleteText, setDeleteText] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteKeyIds, setDeleteKeyIds] = useState<string[]>([]);

  const dispatchRevokedNotification = useDispatchNotification({
    message: 'API keys deleted.',
    variant: 'success',
  });

  const handleOpenCreateModal = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleDeleteKeys = useCallback(async () => {
    if (!deleteKeyIds.length) return;

    await deleteApiKeys(deleteKeyIds);
    dispatchRevokedNotification();
    setDeleteKeyIds([]);
    setDeleteText('');
  }, [deleteApiKeys, deleteKeyIds, dispatchRevokedNotification]);

  const clearDeleteState = useCallback(() => {
    setDeleteKeyIds([]);
    setDeleteText('');
  }, []);

  return (
    <>
      <Helmet>
        <title>API Keys</title>
      </Helmet>

      <div className={classes.listContainer}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <DataGridPremium
          checkboxSelection
          disableColumnFilter
          disableColumnMenu
          disableColumnReorder
          disableColumnResize
          disableRowSelectionOnClick
          hideFooter
          showToolbar
          className={classes.xGrid}
          columns={columns}
          getRowHeight={() => 'auto'}
          getRowId={row => row.id}
          loading={loading}
          rows={apiKeys}
          slotProps={{
            loadingOverlay: {
              noRowsVariant: 'linear-progress',
              variant: 'linear-progress',
            },
            toolbar: {
              isAdmin,
              onCreateApiKey: handleOpenCreateModal,
              onDeleteApiKeys: setDeleteKeyIds,
            },
          }}
          slots={{
            baseCheckbox: AlineCheckbox,
            baseTooltip: GridAlineTooltip,
            noRowsOverlay: ApiKeysEmpty,
            toolbar: ApiKeysToolbar,
          }}
          sx={createDataGridPremiumFooterlessPaperStyles}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        message="Are you sure you want to delete the selected API keys? This action cannot be undone."
        open={Boolean(deleteKeyIds.length)}
        primaryDisabledReason={
          deleteText !== confirmDeleteString ? `Type ${confirmDeleteString} to proceed` : ''
        }
        primaryText="Delete"
        title="Delete API keys?"
        onClickPrimary={handleDeleteKeys}
        onClose={clearDeleteState}
        onExit={clearDeleteState}
      >
        <div className={classes.deleteWrapper}>
          <AlineInput
            autoFocus
            data-cy="confirm-delete-input"
            placeholder={`Type ${confirmDeleteString} to confirm...`}
            value={deleteText}
            onChange={e => setDeleteText(e.target.value)}
          />
        </div>
      </ConfirmationDialog>

      {/* Create API Key Modal */}
      <CreateApiKeyModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onSuccess={handleCreateSuccess}
      />
    </>
  );
}

interface ApiKeysToolbarProps extends GridToolbarProps {
  isAdmin?: boolean;
  onCreateApiKey?: () => void;
  onDeleteApiKeys?: (ids: string[]) => void;
}

function ApiKeysToolbar({
  isAdmin,
  onCreateApiKey,
  onDeleteApiKeys,
}: ApiKeysToolbarProps): JSX.Element {
  const apiRef = useGridApiContext();
  const { search, setSearch } = useDataGridSearch();
  const { classes: toolbarClasses } = useListToolbarStyles();
  const selectionModel = useGridSelector(apiRef, gridRowSelectionIdsSelector);
  const selectedIds = selectionModel
    .keys()
    .map(id => String(id))
    .toArray();
  const hasSelectedKeys = Boolean(selectedIds.length);

  return (
    <div className={toolbarClasses.toolbar}>
      <Spacer />
      <AlineTooltip arrow placement="top" title="Delete API keys">
        <div style={{ alignItems: 'center', display: 'flex' }}>
          <IconButton
            size="small"
            style={{ visibility: hasSelectedKeys ? 'visible' : 'hidden' }}
            onClick={() => onDeleteApiKeys?.(selectedIds)}
          >
            <TrashcanIcon />
          </IconButton>
        </div>
      </AlineTooltip>
      <SearchInput placeholder="Search API keys..." search={search} setSearch={setSearch} />
      <AlineTooltip arrow placement="top" title={isAdmin ? '' : 'Only admins can create API keys'}>
        <span>
          <AlineButton
            color="primary"
            disabled={!isAdmin}
            endIcon={<WhitePlusIcon />}
            variant="contained"
            onClick={onCreateApiKey}
          >
            New API Key
          </AlineButton>
        </span>
      </AlineTooltip>
    </div>
  );
}

function ApiKeysEmpty(): JSX.Element {
  return <ListEmpty subtitle="You haven't created any API keys yet." title="No API keys" />;
}
